import React from 'react';
import { Droppable } from 'react-beautiful-dnd';
import Tooltip from '../ItemTooltip';
import { Item } from '../../types/Item';
import styles from './RuneSocketPanel.module.css';

export interface SocketedRune {
  id: number;
  itemId: number | null;    // item template id (display only)
  itemInstanceId: number | null;  // unique physical copy id (canonical reference)
  equipmentSlot: string;   // deprecated; kept for backward compat
  isEquipped: boolean;     // true = item currently worn in that slot
  runeSlotIndex: number;
  runeItemId: number;
  rune: {
    id: number;
    name: string;
    runeColor: string;
    runeTier: number;
    enchantModifiers: string;
    img?: string;
  };
}

interface SlotDef {
  key: string;           // visual slot key (matches paperDoll slot ids)
  label: string;
}

interface GroupDef {
  label: string;
  slots: SlotDef[];
}

const SLOT_GROUPS: GroupDef[] = [
  {
    label: 'Weapon Runes',
    slots: [
      { key: 'mainHand', label: 'Main Hand' },
      { key: 'offHand',  label: 'Off Hand' },
    ],
  },
  {
    label: 'Armor Runes',
    slots: [
      { key: 'Head',      label: 'Head' },
      { key: 'Shoulders', label: 'Shoulder' },
      { key: 'Torso',     label: 'Torso' },
      { key: 'Hands',     label: 'Hands' },
      { key: 'Legs',      label: 'Legs' },
      { key: 'Feet',      label: 'Feet' },
    ],
  },
  {
    label: 'Accessory Runes',
    slots: [
      { key: 'Neck',    label: 'Neck' },
      { key: 'Amulet',  label: 'Amulet' },
      { key: 'Cloak',   label: 'Cloak' },
      { key: 'Armring', label: 'Armring' },
      { key: 'Ring',    label: 'Ring' },
    ],
  },
];

export const RUNE_COLOR_CSS: Record<string, string> = {
  green: '#4CAF50', orange: '#FF9800', blue: '#2196F3', red: '#f44336',
  aqua: '#00BCD4', yellow: '#FFEB3B', gray: '#9E9E9E', purple: '#9C27B0',
};

interface Props {
  items: Item[];
  socketedRunes: SocketedRune[];
  draggingRuneColor: string | null;
}

const RuneSocketPanel: React.FC<Props> = ({ items, socketedRunes, draggingRuneColor }) => {
  const twoHandItem = items.find(i => i.equipped && i.slot === 'twoHand');

  const getEquippedItem = (slotKey: string): Item | undefined => {
    if (slotKey === 'mainHand') {
      return items.find(i => i.equipped && (i.slot === 'mainHand' || i.slot === 'twoHand'));
    }
    if (slotKey === 'offHand' && twoHandItem) return undefined; // blocked by 2H
    return items.find(i => i.equipped && i.slot === slotKey);
  };

  // Only render if at least one equipped item has rune slots
  const hasAnyRuneSlots = SLOT_GROUPS.some(group =>
    group.slots.some(slotDef => {
      const item = getEquippedItem(slotDef.key);
      return item && Array.isArray(item.runeSlots) && item.runeSlots.length > 0;
    })
  );

  if (!hasAnyRuneSlots) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Rune Sockets</span>
        <span className={styles.panelHint}>Drag runes from inventory to socket them</span>
      </div>

      {SLOT_GROUPS.map(group => {
        const visibleRows = group.slots.filter(slotDef => {
          const item = getEquippedItem(slotDef.key);
          return item && Array.isArray(item.runeSlots) && item.runeSlots.length > 0;
        });
        if (!visibleRows.length) return null;

        return (
          <div key={group.label} className={styles.group}>
            <div className={styles.groupLabel}>{group.label}</div>

            {visibleRows.map(slotDef => {
              const equippedItem = getEquippedItem(slotDef.key)!;
              // equipmentSlot is the actual slot stored in Equipment table
              const equipmentSlot = equippedItem.slot; // 'mainHand', 'twoHand', 'Head', etc.
              const runeSlots = equippedItem.runeSlots as string[];
              const hasMatchingSlot = draggingRuneColor && runeSlots.includes(draggingRuneColor);

              return (
                <div
                  key={slotDef.key}
                  className={[
                    styles.slotRow,
                    draggingRuneColor ? styles.slotRowDragging : '',
                    hasMatchingSlot ? styles.slotRowActive : '',
                  ].join(' ')}
                >
                  <span className={[
                    styles.slotLabel,
                    hasMatchingSlot ? styles.slotLabelActive : '',
                  ].join(' ')}>{slotDef.label}</span>
                  <div className={styles.socketCircles}>
                    {runeSlots.map((slotColor, idx) => {
                      // Match runes by the unique physical-copy id (itemInstanceId) so
                      // swapping items in the same slot never shows stale rune data.
                      const socketed = socketedRunes.find(
                        sr => sr.itemInstanceId != null && equippedItem.itemInstanceId != null
                          ? sr.itemInstanceId === equippedItem.itemInstanceId && sr.runeSlotIndex === idx
                          : sr.equipmentSlot === equipmentSlot && sr.runeSlotIndex === idx
                      );
                      const droppableId = `runeSocket-${equipmentSlot}-${idx}`;
                      const isMatchingDrag = draggingRuneColor === slotColor;

                      if (socketed) {
                        return (
                          <Droppable
                            key={droppableId}
                            droppableId={droppableId}
                            direction="horizontal"
                            isDropDisabled={draggingRuneColor !== null && !isMatchingDrag}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={[
                                  styles.socketDropZone,
                                  isMatchingDrag ? styles.socketDropZoneActive : '',
                                ].join(' ')}
                              >
                                <Tooltip itemId={socketed.rune.id}>
                                  <span
                                    className={[
                                      styles.socketFilled,
                                      isMatchingDrag ? styles.socketPulsing : '',
                                      snapshot.isDraggingOver ? styles.socketHovered : '',
                                    ].join(' ')}
                                    style={{ background: RUNE_COLOR_CSS[socketed.rune.runeColor] || '#555' }}
                                    title={`${socketed.rune.name} (${slotColor} slot)`}
                                  />
                                </Tooltip>
                                <span style={{ display: 'none' }}>{provided.placeholder}</span>
                              </div>
                            )}
                          </Droppable>
                        );
                      }

                      return (
                        <Droppable
                          key={droppableId}
                          droppableId={droppableId}
                          direction="horizontal"
                          isDropDisabled={draggingRuneColor !== null && !isMatchingDrag}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={[
                                styles.socketDropZone,
                                isMatchingDrag ? styles.socketDropZoneActive : '',
                              ].join(' ')}
                            >
                              <span
                                className={[
                                  styles.socketEmpty,
                                  isMatchingDrag ? styles.socketPulsing : '',
                                  snapshot.isDraggingOver ? styles.socketHovered : '',
                                ].join(' ')}
                                style={{ borderColor: RUNE_COLOR_CSS[slotColor] || '#666' }}
                                title={`${slotColor} rune slot (empty)`}
                              />
                              <span style={{ display: 'none' }}>{provided.placeholder}</span>
                            </div>
                          )}
                        </Droppable>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default RuneSocketPanel;
